/**
 * TenantBroadcastBanner — Shows platform broadcasts to the tenant admin.
 * Place inside tenant-scoped pages (e.g., SchoolDetailPage). Requires tenantId.
 * Polls /api/superadmin/tenants/{tid}/broadcasts (admin auth), marks seen on dismiss.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Megaphone, X, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { superAdminApi } from '@/components/superadmin/superAdminApi';

// iter 639 — the text colours here were picked for a dark surface (…-100 on a
// 10% tint) and these banners sit on light admin pages, so every broadcast
// read as an empty coloured box with invisible text. Dark text, light tint.
const SEVERITY_STYLES = {
  info:     { bg: 'bg-sky-50 border-sky-200 text-sky-900',          icon: Info,          iconColor: 'text-sky-600' },
  success:  { bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', icon: CheckCircle2, iconColor: 'text-emerald-600' },
  warning:  { bg: 'bg-amber-50 border-amber-200 text-amber-900',     icon: AlertTriangle, iconColor: 'text-amber-600' },
  critical: { bg: 'bg-rose-50 border-rose-200 text-rose-900',        icon: AlertTriangle, iconColor: 'text-rose-600' },
};

export const TenantBroadcastBanner = ({ tenantId }) => {
  const qc = useQueryClient();
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const { data: broadcasts = [] } = useQuery({
    queryKey: ['tenant-broadcasts', tenantId],
    queryFn: () => superAdminApi.tenantBroadcasts(tenantId, true),
    enabled: !!tenantId,
    refetchInterval: 120000,
  });

  const markSeen = useMutation({
    mutationFn: (bcId) => superAdminApi.markBroadcastSeen(tenantId, bcId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-broadcasts', tenantId] }),
  });

  const dismiss = (bcId) => {
    setDismissedIds((s) => new Set([...s, bcId]));
    markSeen.mutate(bcId);
  };

  const visible = (broadcasts || []).filter((b) => !dismissedIds.has(b.id)).slice(0, 3);
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="tenant-broadcast-banner">
      {visible.map((b) => {
        const s = SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.info;
        const Icon = s.icon;
        return (
          <div key={b.id} className={`rounded-lg border px-4 py-2.5 flex items-start gap-3 ${s.bg}`}>
            <Megaphone className={`h-4 w-4 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${s.iconColor}`} />
                <p className="text-sm font-semibold truncate">{b.title}</p>
              </div>
              <p className="text-xs mt-1 opacity-90">{b.body}</p>
              {b.cta_label && b.cta_url && (
                <a href={b.cta_url} target="_blank" rel="noreferrer"
                   className="inline-block mt-1.5 text-xs font-semibold underline">
                  {b.cta_label} →
                </a>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => dismiss(b.id)}
                    className="h-7 w-7 p-0 opacity-70 hover:opacity-100 hover:bg-white/10"
                    data-testid={`broadcast-dismiss-${b.id}`}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};
